import React from 'react';
import testIds from '@smart-tv/e2e/shared/variables';
import { FocusContext } from '@noriginmedia/norigin-spatial-navigation';
import Fade from '@smart-tv/components/fade/fade';
import styles from './player-channels.module.scss';
import usePlayerChannels from './use-player-channels';
import PlayerChannel from '../player-channel/player-channel';
import { usePlayerContext } from '../_contexts/player-context';
import useVodPlayer from '../vod-wrapper/_hooks/use-vod-player';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import clsx from 'clsx';

export interface Props {
  open: boolean;
  visibleBrowser?: boolean;
  onClose: () => void;
  handleChannelChange?: (newChannelSid: number) => void;
}

export const PlayerChannels: React.FC<Props> = (props: Props) => {
  const { open, onClose, handleChannelChange } = props;
  const { channels } = usePlayerContext();
  const { streamOptions } = useVodPlayer();

  const currentSid = streamOptions?.streamParams?.sid;

  const {
    handleKeyDown,
    handleKeyUp,
    handleMouseEnter,
    handleMouseLeave,
    handleChannelFocus,
    scrollingRef,
    rootRef
  } = usePlayerChannels({ open, onClose });

  const content = (
    <div
      ref={rootRef}
      tabIndex={1}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => e.stopPropagation()}
      data-testid={testIds.player.channels.root}
      className={clsx(styles['player-channels'], {
        [styles['player-channels--browser-visible']]: props.visibleBrowser
      })}
    >
      <ul
        ref={scrollingRef}
        className={clsx(styles['player-channels__container'], {
          [styles['player-channels__container--browser']]: IS_BROWSER
        })}
      >
        {channels?.map((channel, index) => (
          <PlayerChannel
            key={channel.sid}
            channel={channel}
            isFocused={open && channel.sid === currentSid}
            isFirst={index === 0}
            selected={channel.sid === currentSid}
            onEnterPress={(channelSid: number) => {
              handleChannelChange?.(channelSid);
            }}
            onFocus={handleChannelFocus}
          />
        ))}
      </ul>
    </div>
  );

  return (
    <FocusContext.Provider value={'PlayerChannels'}>
      {IS_BROWSER ? content : <Fade in={open}>{content}</Fade>}
    </FocusContext.Provider>
  );
};

export default React.memo(PlayerChannels);
